import { ArrowsRightLeftIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import { Button } from "@/core/components/ui/button";
import { Stack } from "@/core/components/ui/stack";
import { toast } from "@/core/components/ui/toast";
import {
  TooltipContent,
  TooltipTrigger,
  Tooltip,
} from "@/core/components/ui/tooltip/tooltip";
import { Typography } from "@/core/components/ui/typography";
import { useImpersonation } from "@/core/impersonation";

interface ImpersonationBannerProps {
  onSwitchUser?: () => void;
}

export const ImpersonationBanner = ({
  onSwitchUser,
}: ImpersonationBannerProps) => {
  const { isImpersonating, impersonatedUser, endImpersonation } =
    useImpersonation();
  const navigate = useNavigate();

  const handleEndImpersonation = async () => {
    try {
      await endImpersonation();
      navigate("/");
    } catch (error) {
      toast.error("Failed to end impersonation", {
        description:
          error instanceof Error ? error.message : "Please try again",
      });
    }
  };

  if (!isImpersonating || !impersonatedUser) {
    return null;
  }

  const userName =
    impersonatedUser.first_name && impersonatedUser.last_name
      ? `${impersonatedUser.first_name} ${impersonatedUser.last_name}`
      : impersonatedUser.email;

  const parentName = impersonatedUser.parentable_name;

  const parentType = impersonatedUser.parentable_type;

  return (
    <Stack
      direction="row"
      align="center"
      justify="between"
      // Level with the nav and painted after it, so it sits over the bar; dialogs portal
      // to the end of the body and still cover it, which a modal should. It used to carry
      // an inline zIndex of 555 and covered everything, including modals.
      className="fixed left-3 top-2 z-50 h-[52px] w-auto rounded-full py-1 pl-8"
      style={{
        background: "var(--brand-gradient)",
        opacity: "100%",
      }}
      fullWidth
    >
      <Stack direction="row" align="center" className="gap-md">
        <Typography variant="subtitleW1" className="font-semibold text-white">
          Acting as
        </Typography>
        <Stack direction="column" align="start" className="gap-[0px]">
          <Typography variant="bodyW1" className="text-white">
            {userName}
          </Typography>
          <Tooltip>
            <TooltipTrigger>
              <Typography
                variant="bodyW2"
                className="max-w-[250px] overflow-hidden text-ellipsis whitespace-nowrap text-white opacity-90"
              >
                {parentName && <span>{parentName}</span>}({parentType})
              </Typography>
            </TooltipTrigger>
            <TooltipContent style={{ zIndex: 9999 }}>
              {parentName && <span>{parentName}</span>}({parentType})
            </TooltipContent>
          </Tooltip>
        </Stack>
      </Stack>

      <Stack direction="row" align="center" className="gap-0 pr-2">
        {onSwitchUser && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="unstyled"
                size="sm"
                onClick={onSwitchUser}
                aria-label="Switch user"
                leftIcon={<ArrowsRightLeftIcon className="h-4 w-4" />}
                className="px-2 py-0 text-white opacity-80 hover:opacity-100"
              />
            </TooltipTrigger>
            <TooltipContent style={{ zIndex: 9999 }}>
              Switch user
            </TooltipContent>
          </Tooltip>
        )}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="unstyled"
              size="sm"
              onClick={handleEndImpersonation}
              aria-label="Stop impersonating"
              leftIcon={<XMarkIcon className="h-4 w-4" />}
              className="px-2 py-0 text-white opacity-80 hover:opacity-100"
            />
          </TooltipTrigger>
          <TooltipContent style={{ zIndex: 9999 }}>
            Stop impersonating
          </TooltipContent>
        </Tooltip>
      </Stack>
    </Stack>
  );
};
